import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Instagram } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const ContactPage = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Simulate form submission
        await new Promise(resolve => setTimeout(resolve, 1000));

        toast.success('Message sent successfully!', {
            description: 'We will get back to you as soon as possible.'
        });

        setFormData({ name: '', email: '', subject: '', message: '' });
        setIsSubmitting(false);
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="pt-24 pb-16"
        >
            <div className="max-w-5xl mx-auto px-6 lg:px-8">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-16"
                >
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-foreground mb-4 italic">
                        Contact Us
                    </h1>
                    <p className="font-body text-muted-foreground text-lg max-w-xl mx-auto">
                        Have a question about our collection or interested in a specific specimen? We&apos;d love to hear from you.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                    {/* Get in Touch - Contact Form */}
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        <h2 className="font-display text-2xl text-foreground mb-8 italic">
                            Get in Touch
                        </h2>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="space-y-2">
                                <Label htmlFor="name" className="font-body text-sm text-foreground">Name</Label>
                                <Input
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    className="rounded-none border-border bg-background h-12"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="email" className="font-body text-sm text-foreground">Email</Label>
                                <Input
                                    id="email"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    className="rounded-none border-border bg-background h-12"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="subject" className="font-body text-sm text-foreground">Subject</Label>
                                <select
                                    id="subject"
                                    name="subject"
                                    value={formData.subject}
                                    onChange={handleChange}
                                    required
                                    className="w-full h-12 px-3 border border-border bg-background font-body text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                                >
                                    <option value="">Select a topic</option>
                                    <option value="general">General Inquiry</option>
                                    <option value="collection">Collection Question</option>
                                    <option value="specimen">Specimen Interest</option>
                                    <option value="collaboration">Collaboration</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="message" className="font-body text-sm text-foreground">Message</Label>
                                <Textarea
                                    id="message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    rows={5}
                                    required
                                    className="rounded-none border-border bg-background resize-none"
                                />
                            </div>

                            <Button 
                                type="submit" 
                                disabled={isSubmitting}
                                className="w-full h-12 rounded-none bg-primary hover:bg-primary/90 text-primary-foreground font-body text-sm tracking-wider-custom uppercase"
                            >
                                {isSubmitting ? 'Sending...' : 'Send Message'}
                            </Button>
                        </form>
                    </motion.div>

                    {/* Information */}
                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        <h2 className="font-display text-2xl text-foreground mb-8 italic">
                            Information
                        </h2>
                        
                        <div className="space-y-6 mb-10">
                            {/* Email */}
                            <div className="flex items-start gap-4">
                                <Mail className="w-5 h-5 text-primary mt-1" />
                                <div>
                                    <p className="font-display text-lg text-foreground italic">Email</p>
                                    <a 
                                        href="mailto:hello@paigeminerals.com"
                                        className="font-body text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        hello@paigeminerals.com
                                    </a>
                                </div>
                            </div>

                            {/* Location */}
                            <div className="flex items-start gap-4">
                                <MapPin className="w-5 h-5 text-primary mt-1" />
                                <div>
                                    <p className="font-display text-lg text-foreground italic">Location</p>
                                    <p className="font-body text-muted-foreground">
                                        Denver, Colorado
                                    </p>
                                </div>
                            </div>

                            {/* Instagram */}
                            <div className="flex items-start gap-4">
                                <Instagram className="w-5 h-5 text-primary mt-1" />
                                <div>
                                    <p className="font-display text-lg text-foreground italic">Instagram</p>
                                    <a 
                                        href="https://instagram.com/paigeminerals"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-body text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        @paigeminerals
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Interested in a Specimen box */}
                        <div className="bg-secondary/50 p-6 border-t-2 border-primary">
                            <h3 className="font-display text-xl text-foreground mb-3 italic">
                                Interested in a Specimen?
                            </h3>
                            <p className="font-body text-sm text-muted-foreground mb-4 leading-relaxed">
                                While our personal collection is not for sale, we occasionally have select pieces available for acquisition. Visit our shop to see what&apos;s currently available.
                            </p>
                            <Link 
                                to="/shop"
                                className="font-body text-sm tracking-wider-custom uppercase text-primary hover:text-primary/80 transition-colors border-b border-primary pb-0.5"
                            >
                                View Available Pieces
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </div>
        </motion.div>
    );
};

export default ContactPage;
